import { ALL_AUTOMATION_PLACEHOLDERS } from "@/components/automations/utils/subject-placeholders"
import { EMAIL_PLACEHOLDERS, SEQUENCE_RECIPIENT_PLACEHOLDERS } from "@/components/email/email-placeholders"

let dynamicPlaceholders: PlaceholderData[] = []

function mergePlaceholders(existing: PlaceholderData[], incoming: PlaceholderData[]) {
  if (!incoming.length) return existing

  const byId = new Map(existing.map((p) => [p.id, p]))

  for (const placeholder of incoming) {
    byId.set(placeholder.id, placeholder)
  }

  return Array.from(byId.values())
}

export function registerPlaceholders(placeholders: PlaceholderData[] = []) {
  if (!placeholders.length) return

  dynamicPlaceholders = mergePlaceholders(dynamicPlaceholders, placeholders)
}

export interface PlaceholderData {
  id: string
  label: string
  value: string
  category: string
  description?: string
  displayValue?: string
  // Value interpolates to raw HTML, so it should only be offered in rich-text
  // (HTML) editors — never in plain-text fields like subjects, SMS, or titles.
  richTextOnly?: boolean
}

export const INTERNAL_EMAIL_PLACEHOLDERS: PlaceholderData[] = [
  // Household attributes
  { id: "household_name", label: "Name", value: "{{ household.name }}", category: "Household" },
  { id: "household_type", label: "Type", value: "{{ household.type }}", category: "Household" },
  { id: "household_tier", label: "Tier", value: "{{ household.tier }}", category: "Household" },
  { id: "household_tags", label: "Tags", value: "{{ household.tags }}", category: "Household" },
  {
    id: "household_became_client_at",
    label: "Became client at",
    value: "{{ household.became_client_at }}",
    category: "Household",
  },
  {
    id: "household_next_review_due_on",
    label: "Next review due on",
    value: "{{ household.next_review_due_on }}",
    category: "Household",
  },
  { id: "household_ai_summary", label: "AI summary", value: "{{ household.ai_summary }}", category: "Household" },
  {
    id: "household_next_meeting_at",
    label: "Next meeting start time",
    description: 'Example: "Saturday, February 7 at 8:30am"',
    value: "{{ household.next_meeting_at }}",
    category: "Household",
  },
  {
    id: "household_last_meeting_date",
    label: "Last meeting date",
    description: 'Example: "February 7, 2026"',
    value: "{{ household.last_meeting_date }}",
    category: "Household",
  },
  {
    id: "household_next_meeting_date",
    label: "Next meeting date",
    description: 'Example: "February 7, 2026"',
    value: "{{ household.next_meeting_date }}",
    category: "Household",
  },

  // Head of household attributes
  {
    id: "hoh_first_name",
    label: "First name",
    value: "{{ household.head_of_household.first_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_last_name",
    label: "Last name",
    value: "{{ household.head_of_household.last_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_full_name",
    label: "Full name",
    value: "{{ household.head_of_household.full_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_preferred_name",
    label: "Preferred name",
    value: "{{ household.head_of_household.preferred_name }}",
    category: "Head of household",
  },
  {
    id: "hoh_job_title",
    label: "Job title",
    value: "{{ household.head_of_household.job_title }}",
    category: "Head of household",
  },
  {
    id: "hoh_household_role",
    label: "Household role",
    value: "{{ household.head_of_household.household_role }}",
    category: "Head of household",
  },
  {
    id: "hoh_gender",
    label: "Gender",
    value: "{{ household.head_of_household.gender }}",
    category: "Head of household",
  },
  {
    id: "hoh_marital_status",
    label: "Marital status",
    value: "{{ household.head_of_household.marital_status }}",
    category: "Head of household",
  },
  {
    id: "hoh_date_of_birth",
    label: "Date of birth",
    value: "{{ household.head_of_household.date_of_birth }}",
    category: "Head of household",
  },
  {
    id: "hoh_email_addresses",
    label: "Email addresses",
    value: "{{ household.head_of_household.string_email_addresses }}",
    category: "Head of household",
  },
  {
    id: "hoh_phone_numbers",
    label: "Phone numbers",
    value: "{{ household.head_of_household.string_phone_numbers }}",
    category: "Head of household",
  },
  {
    id: "hoh_addresses",
    label: "Addresses",
    value: "{{ household.head_of_household.string_addresses }}",
    category: "Head of household",
  },
  {
    id: "hoh_initials",
    label: "Initials",
    value: "{{ household.head_of_household.initials }}",
    category: "Head of household",
  },
]

export const SEQUENCE_EMAIL_PLACEHOLDERS = [...EMAIL_PLACEHOLDERS, ...SEQUENCE_RECIPIENT_PLACEHOLDERS]

export const LIQUID_PLACEHOLDER_REGEX =
  /\{\{\s*[a-zA-Z_][a-zA-Z0-9_-]*(?:\s*\.\s*[a-zA-Z0-9_-]+)*(?:\s*\|[^{}]*)?\s*\}\}/g

export const normalizeLiquid = (value: string) =>
  value
    .replace(/\{\{\s*/g, "{{")
    .replace(/\s*\}\}/g, "}}")
    .replace(/\s*\.\s*/g, ".")
    .replace(/\s*\|\s*/g, "|")

const ALL_PLACEHOLDERS: PlaceholderData[] = [
  ...SEQUENCE_EMAIL_PLACEHOLDERS,
  ...INTERNAL_EMAIL_PLACEHOLDERS,
  ...ALL_AUTOMATION_PLACEHOLDERS,
]

export function findPlaceholderByNormalizedValue(value: string, placeholders?: PlaceholderData[]) {
  const searchIn = placeholders ?? [...dynamicPlaceholders, ...ALL_PLACEHOLDERS]
  const normalizedValue = normalizeLiquid(value)
  return searchIn.find((p) => normalizeLiquid(p.value) === normalizedValue)
}

export function findPlaceholderById(id: string | null | undefined, placeholders?: PlaceholderData[]) {
  if (!id) return undefined
  const searchIn = placeholders ?? [...dynamicPlaceholders, ...ALL_PLACEHOLDERS]
  return searchIn.find((p) => p.id === id)
}
